/**
 * @description
 * API key service for managing user and client API keys.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";
import type { ApiKey, CreateApiKeyRequest } from "./types";

/**
 * API key service for managing API keys
 */
export class ApiKeyService extends BaseAPIClient {
  /**
   * Get user's API keys
   */
  async getUserApiKeys(
    clientId: string,
    userId: string,
    options?: {
      provider?: string;
      active?: boolean;
    },
  ): Promise<{ apiKeys: ApiKey[] }> {
    const params = new URLSearchParams();
    if (options?.provider) params.set("provider", options.provider);
    if (options?.active !== undefined)
      params.set("active", options.active.toString());

    const queryString = params.toString();
    const endpoint = `/clients/${clientId}/users/${userId}/api-keys${queryString ? `?${queryString}` : ""}`;

    return this.request<{ apiKeys: ApiKey[] }>(endpoint);
  }

  /**
   * Add/update API key for user
   */
  async addUserApiKey(
    clientId: string,
    userId: string,
    keyData: CreateApiKeyRequest,
  ): Promise<{ apiKey: ApiKey }> {
    return this.request<{ apiKey: ApiKey }>(
      `/clients/${clientId}/users/${userId}/api-keys`,
      {
        method: "POST",
        body: JSON.stringify(keyData),
      },
    );
  }

  /**
   * Delete API key
   */
  async deleteUserApiKey(
    clientId: string,
    userId: string,
    keyId: string,
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      `/clients/${clientId}/users/${userId}/api-keys/${keyId}`,
      {
        method: "DELETE",
      },
    );
  }

  /**
   * Get available API keys for a client
   */
  async getAvailableApiKeys(clientId: string): Promise<{ id: string; service: string }[]> {
    const data = await this.request<{ keys: { id: string; service: string }[] }>(
      `/clients/${clientId}/api-keys?status=unassigned`
    );
    return data.keys;
  }

  /**
   * Get API keys for a client
   */
  async getClientApiKeys(clientId: string): Promise<{ id: string; service: string; assignedToUserId: string | null; createdAt: string }[]> {
    const data = await this.request<{ keys: { id: string; service: string; assignedToUserId: string | null; createdAt: string }[] }>(
      `/clients/${clientId}/api-keys`
    );
    return data.keys;
  }

  /**
   * Add an API key for a client
   */
  async addClientApiKey(
    clientId: string,
    data: { service: string; apiKey: string }
  ): Promise<{ success: boolean; id: string }> {
    return this.request(`/clients/${clientId}/api-keys`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete an API key for a client
   */
  async deleteClientApiKey(clientId: string, keyId: string): Promise<{ success: boolean }> {
    return this.request(`/clients/${clientId}/api-keys/${keyId}`, {
      method: 'DELETE',
    });
  }
}

// Export singleton instance
export const apiKeyService = new ApiKeyService();
